import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext(null);

// Create an axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
  headers: {
    'Content-Type': 'application/json'
  }
});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true,
  });

  // Logout function
  const logout = useCallback(() => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      
      setAuth({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  // Setup axios interceptors for requests and responses
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('401 error detected, logging out');
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [logout]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        if (token && user) {
          const parsedUser = JSON.parse(user);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Verify token with backend
          try {
            await api.get('/api/auth/verify'); // Add this endpoint to your backend
            setAuth({
              isAuthenticated: true,
              user: parsedUser,
              token,
              loading: false,
            });
            console.log('Auth initialized with valid token');
          } catch (error) {
            console.log('Token verification failed, logging out');
            logout();
          }
        } else {
          setAuth(prev => ({ ...prev, loading: false }));
          console.log('No stored credentials found');
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuth(prev => ({ ...prev, loading: false }));
      }
    };

    initializeAuth();
  }, [logout]);

  // Login function
  const login = useCallback(async (credentials) => {
    try {
      const response = await api.post('/api/auth/login', credentials);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setAuth({
        isAuthenticated: true,
        user,
        token,
        loading: false,
      });

      console.log('Login successful');
      return true;
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      throw error;
    }
  }, []);

  // Update user function
  const updateUser = useCallback(async (userData) => {
    try {
      const response = await api.put('/api/auth/update-profile', userData);
      const updatedUser = response.data.user;
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setAuth(prev => ({
        ...prev,
        user: updatedUser,
      }));

      return updatedUser;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }, []);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      const response = await api.post('/api/auth/register', userData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setAuth({
        isAuthenticated: true,
        user,
        token,
        loading: false,
      });

      console.log('Registration successful');
      return true;
    } catch (error) {
      console.error('Registration error:', error.response?.data || error.message);
      throw error;
    }
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('Auth state changed:', auth);
  }, [auth]);

  const value = {
    auth,
    setAuth,
    login,
    logout,
    updateUser,
    register,
    isLoading: auth.loading,
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    token: auth.token
  };

  return (
    <AuthContext.Provider value={value}>
      {!auth.loading && children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export the api instance for use in other components
export { api };